import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsAPI } from "@/lib/notifications-api";
import { queryKeys } from "@/lib/query-keys";
import { useAuthStore } from "@/stores/authStore";
import { useNotificationsStore } from "@/stores/notifications-store";
import { notificationSound } from "@/lib/notification-sound";
import { faviconBadge } from "@/lib/favicon-badge";
import { useToast } from "@/hooks/use-toast";

/**
 * App-wide notifications for incoming nudges.
 *
 * Mounted once in the authenticated shell so a provider is alerted on every
 * page, not only while the Pings screen happens to be open.
 *
 * The count is the backend's `unread_count` from `/nudges/console/unread-count/`
 * — conversations, not messages, so four patients waiting reads "4". It is the
 * single source for the bell, the favicon badge and the arrival alert, so the
 * three can never disagree. Per-tab chips inside Pings keep their own counts.
 *
 * TRANSPORT — deliberately interim. This polls the unread-count endpoint
 * rather than opening an Ably subscription: a per-user channel is the right
 * shape but needs a backend publisher and a scoped Ably token first (the key is
 * currently inlined into the public bundle), and no such endpoint exists yet.
 * When it ships, replace the query below with a subscription and call the
 * alert from its handler. Everything downstream stays as-is.
 */

const POLL_INTERVAL_MS = 30_000;

export const usePingNotifications = () => {
  const { user, isAuthenticated } = useAuthStore();
  const soundEnabled = useNotificationsStore((s) => s.soundEnabled);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Null until the first successful read, so mounting never fires a
  // notification for chats that were already unread before this session.
  const lastCountRef = useRef<number | null>(null);
  const lastEventCountRef = useRef<number | null>(null);

  // Read the latest value inside the effect without making it a dependency,
  // which would re-run the comparison on every mute toggle.
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;

  // Unlock audio on the first user gesture; without this the first sound of
  // the session is swallowed by the browser's autoplay policy.
  useEffect(() => {
    notificationSound.prime();
  }, []);

  const { data } = useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationsAPI.unreadCount(),
    enabled: !!user && isAuthenticated,
    retry: false,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
    gcTime: 300000,
  });

  const unreadCount = data?.unread_count;
  const unreadEventCount = data?.unread_event_count;

  useEffect(() => {
    if (unreadCount === undefined || unreadEventCount === undefined) return;

    const previousEvents = lastEventCountRef.current;
    lastEventCountRef.current = unreadEventCount;

    // The badge counts conversations, so the favicon follows it.
    faviconBadge.setBadgeCount(unreadCount);

    // Either figure moved, so an open inbox is stale: refetch it rather than
    // leaving the bell and the list disagreeing until the next navigation.
    const previousCount = lastCountRef.current;
    lastCountRef.current = unreadCount;
    if (
      previousEvents !== null &&
      (unreadEventCount !== previousEvents || unreadCount !== previousCount)
    ) {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.notifications.all, "list"],
      });
    }

    // Alert on arriving MESSAGES, not on arriving conversations. A second
    // message in a chat that is already unread leaves the conversation count
    // at 1, so keying the alert on it would stay silent for exactly the case a
    // provider most needs to hear about. The bell stays on conversations.
    if (previousEvents === null || unreadEventCount <= previousEvents) return;

    const arrived = unreadEventCount - previousEvents;

    if (soundEnabledRef.current) {
      notificationSound.play();
    }

    toast({
      title: arrived === 1 ? "New ping" : `${arrived} new pings`,
      description:
        arrived === 1
          ? "A patient or teammate has messaged you."
          : "You have unread messages waiting.",
    });
  }, [unreadCount, unreadEventCount, toast, queryClient]);

  // Clear the badge when the shell unmounts (logout) so it never goes stale.
  useEffect(() => () => faviconBadge.setBadgeCount(0), []);

  return { unreadCount: unreadCount ?? 0 };
};
